import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import makeAsyncScriptLoader from 'react-async-script';

// Created from https://cloud.google.com/recaptcha-enterprise/docs/api-ref-checkbox-keys#attributes_parameters
type RenderOptions = {
  sitekey: string;
  size?: 'compact' | 'normal' | 'invisible'; // invisible not in the def, but we've added it?
  action?: string;
  callback?: string | ((token: string | null) => void);
  'expired-callback'?: string | (() => void);
  'error-callback'?: string | (() => void);
};

export type GReCAPTCHA = {
  enterprise: {
    execute(id: number): void;
    reset(id: number): void;
    render<T>(node: T, options: RenderOptions): number;
  };
};

type Props = {
  sitekey: RenderOptions['sitekey'];
  grecaptcha?: GReCAPTCHA;
  size?: RenderOptions['size'];
  onChange?: (token: string | null) => void;
  onExpired?: () => void;
  onErrored?: () => void;
};

type ExecutionRef = {
  onResolve?: (token: string) => void;
  onReject?: () => void;
  hasRequested: boolean;
  resetRequired: boolean;
};

export type ReCAPTCHAEnterpriseHandle = {
  executeAsync: () => Promise<string>;
};

export const ReCAPTCHAEnterprise = forwardRef<ReCAPTCHAEnterpriseHandle, Props>((props, ref) => {
  const { grecaptcha, onChange, onErrored, onExpired, size, sitekey } = props;
  const enterprise = grecaptcha?.enterprise;

  const [widgetId, setWidgetId] = useState<number | undefined>();

  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * The execution callbacks and states (hasRequest, resetRequired) etc.
   * are being tracked in a useRef since we don't want to react to its changes
   * but need to track it in-between renders.
   */
  const executionRef = useRef<ExecutionRef>({
    hasRequested: false,
    resetRequired: false,
  });

  const resetExecutionHandlers = useCallback(() => {
    delete executionRef.current.onResolve;
    delete executionRef.current.onReject;
  }, []);

  const reset = useCallback(() => {
    if (enterprise && typeof widgetId === 'number') {
      enterprise.reset(widgetId);
    }
  }, [enterprise, widgetId]);

  const handleError = useCallback(() => {
    if (onErrored) {
      onErrored();
    }
    if (executionRef.current?.onReject) {
      executionRef.current.onReject();
      resetExecutionHandlers();
    }
  }, [onErrored, resetExecutionHandlers]);

  const handleChange = useCallback(
    (token: string | null) => {
      if (onChange) {
        onChange(token);
      }

      if (!token) {
        handleError();
      } else if (executionRef.current?.onResolve) {
        executionRef.current.onResolve(token);
        resetExecutionHandlers();
      }
    },
    [handleError, onChange, resetExecutionHandlers]
  );

  const handleExpired = useCallback(() => {
    if (onExpired) {
      onExpired();
      if (executionRef.current?.onReject) {
        executionRef.current.onReject();
        resetExecutionHandlers();
      }
    } else {
      handleChange(null);
    }
  }, [handleChange, onExpired, resetExecutionHandlers]);

  const execute = useCallback(
    (targetWidgetId?: number) => {
      const id = typeof targetWidgetId === 'undefined' ? widgetId : targetWidgetId;

      if (enterprise && id !== undefined) {
        // Tokens are cached, reset is needed after first execute
        if (executionRef.current?.resetRequired) {
          reset();
        }
        executionRef.current.resetRequired = true;
        enterprise.execute(id);
      } else {
        executionRef.current.hasRequested = true;
      }
    },
    [enterprise, reset, widgetId]
  );

  const renderExplicit = useCallback(() => {
    if (!sitekey) {
      console.warn('ReCAPTCHAEnterprise: recaptcha enterprise site key not provided');
      return;
    }

    // Check if grecaptcha is available and if it hasn't been rendered already
    if (!enterprise?.render || widgetId !== undefined) {
      return;
    }

    const wrapper = document.createElement('div');

    const id = enterprise.render<HTMLDivElement>(wrapper, {
      sitekey,
      size,
      callback: handleChange,
      'expired-callback': handleExpired,
      'error-callback': handleError,
    });

    setWidgetId(id);

    containerRef.current!.appendChild(wrapper);

    if (executionRef.current?.hasRequested) {
      executionRef.current.hasRequested = false;
      execute(id);
    }
  }, [execute, enterprise, handleChange, handleError, handleExpired, sitekey, size, widgetId]);

  useEffect(() => {
    /**
     * This has to run on every render, hence why there is no dependency array.
     * renderExplicit was called on componentDidMount & componentDidUpdate when it was a class component.
     */
    renderExplicit();
  });

  useEffect(() => {
    const delayOfCaptchaIframeRemoving = () => {
      const temporaryNode = document.createElement('div');
      document.body.appendChild(temporaryNode);
      temporaryNode.style.display = 'none';

      // move of the recaptcha to a temporary node

      while (containerRef.current?.firstChild) {
        temporaryNode.appendChild(containerRef.current.firstChild);
      }

      // delete the temporary node after reset will be done
      setTimeout(() => document.body.removeChild(temporaryNode), 5000);
    };

    return () => {
      if (widgetId !== undefined) {
        delayOfCaptchaIframeRemoving();
        reset();
      }
    };
  }, [reset, widgetId]);

  useImperativeHandle(
    ref,
    () => ({
      executeAsync() {
        return new Promise<string>((resolve, reject) => {
          executionRef.current.onResolve = resolve;
          executionRef.current.onReject = reject;
          execute();
        });
      },
    }),
    [execute]
  );

  return <div data-testid="ReCAPTCHAEnterprise" ref={containerRef} />;
});

export default makeAsyncScriptLoader<Props, ReCAPTCHAEnterpriseHandle>(
  () => 'https://www.recaptcha.net/recaptcha/enterprise.js?onload=onloadcallback&render=explicit',
  {
    callbackName: 'onloadcallback',
    globalName: 'grecaptcha',
  }
)(ReCAPTCHAEnterprise);
